// Lightweight WebSocket client for chat-async endpoint
// Server protocol (summary):
// - Client -> Server: { type: 'request', body }, { type: 'ack', id }
//   (No client pings; rely on WebSocket transport for liveness)
// - Server -> Client: { type: 'message', id, ts, kind: 'message'|'error'|'pong'|'system', payload: any, job_id?: string }
import { getWsBase, getOrCreateUserId } from '@/utils/session';
import { getStoredAuthToken } from '@/utils/userApi';
function debugEnabled() {
    try {
        const p = globalThis?.process?.env?.DEBUG_HTTP;
        if (p && String(p).toLowerCase() === 'true')
            return true;
        const w = globalThis?.__ENV__?.DEBUG_HTTP;
        if (w && String(w).toLowerCase() === 'true')
            return true;
    }
    catch { }
    return false;
}
export class ChatAsyncWs {
    ws;
    url;
    onMessage;
    openResolvers = [];
    clientInitiatedClose = false;
    constructor(params) {
        const base = (params?.baseUrl || getWsBase()).replace(/\/$/, '');
        const token = params?.token || getStoredAuthToken() || undefined;
        const userId = params?.userId || getOrCreateUserId();
        const qs = new URLSearchParams();
        // Browser WS cannot add Authorization headers; pass token as query param if available.
        if (token)
            qs.set('token', token);
        // Also provide user_id as fallback for local/dev or if server requires it explicitly.
        if (userId)
            qs.set('user_id', userId);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        this.url = `${base}/ws/chat-async${suffix}`;
        if (debugEnabled()) {
            try {
                console.log('[ChatWS] URL =', this.url);
            }
            catch { }
        }
    }
    connect(handler, options) {
        this.onMessage = handler;
        this.ws = new WebSocket(this.url);
        this.clientInitiatedClose = false;
        this.ws.onopen = () => {
            if (debugEnabled()) {
                try {
                    console.log('[ChatWS] open');
                }
                catch { }
            }
            try {
                options?.onOpen?.();
            }
            catch { }
            // resolve waiters
            const resolvers = this.openResolvers.slice();
            this.openResolvers = [];
            resolvers.forEach((r) => { try {
                r();
            }
            catch { } });
        };
        this.ws.onmessage = (ev) => {
            try {
                const msg = JSON.parse(ev.data);
                if (msg && msg.type === 'message') {
                    // Immediately ack if id is present to support at-least-once delivery semantics
                    if (msg.id) {
                        try {
                            this.send({ type: 'ack', id: msg.id });
                        }
                        catch { }
                    }
                    this.onMessage && this.onMessage(msg);
                }
            }
            catch (e) {
                console.error('[ChatWS] parse error', e, ev.data);
            }
        };
        this.ws.onerror = (ev) => {
            if (debugEnabled()) {
                try {
                    console.log('[ChatWS] error', ev);
                }
                catch { }
            }
            try {
                options?.onError?.(ev);
            }
            catch { }
        };
        this.ws.onclose = (ev) => {
            if (debugEnabled()) {
                try {
                    const initiator = this.clientInitiatedClose ? 'client' : 'server';
                    console.warn('[ChatWS] close', { initiator, code: ev?.code, reason: ev?.reason, wasClean: ev?.wasClean });
                }
                catch { }
            }
            try {
                options?.onClose?.();
            }
            catch { }
        };
    }
    send(obj) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN)
            throw new Error('WebSocket not open');
        this.ws.send(JSON.stringify(obj));
    }
    sendRequest(body) {
        this.send({ type: 'request', body });
    }
    isOpen() {
        return !!this.ws && this.ws.readyState === WebSocket.OPEN;
    }
    waitUntilOpen() {
        if (this.isOpen())
            return Promise.resolve();
        return new Promise((resolve) => { this.openResolvers.push(resolve); });
    }
    close() {
        try {
            this.clientInitiatedClose = true;
            this.ws?.close();
        }
        catch { }
    }
}
export class ChatAsyncManager {
    ws = null;
    handler = null;
    lastTokenSnapshot;
    status = { state: 'closed', attempts: 0 };
    statusListeners = new Set();
    shouldReconnect = true;
    reconnectAttempts = 0;
    maxReconnectAttempts = 3;
    reconnectTimer = null;
    setHandler(h) {
        this.handler = h;
    }
    ensureConnected(force) {
        const currentToken = getStoredAuthToken() || undefined;
        const tokenChanged = this.lastTokenSnapshot !== currentToken;
        // If we already have a socket and it's CONNECTING or OPEN, avoid recreating unless forced or token changed
        if (this.ws && this.ws.ws) {
            try {
                const rs = this.ws.ws.readyState;
                const isConnectingOrOpen = rs === WebSocket.CONNECTING || rs === WebSocket.OPEN;
                if (!force && !tokenChanged && isConnectingOrOpen) {
                    return; // keep existing connection attempt
                }
            }
            catch { }
        }
        else if (this.ws && this.ws.isOpen && this.ws.isOpen()) {
            if (!force && !tokenChanged)
                return;
        }
        // Recreate connection when token changed or forced or socket missing/closed
        if (this.ws) {
            try {
                this.ws.close?.();
            }
            catch { }
            this.ws = null;
        }
        const inst = new ChatAsyncWs();
        // update status
        this.setStatus({ state: this.reconnectAttempts > 0 ? 'reconnecting' : 'connecting', attempts: this.reconnectAttempts });
        this.shouldReconnect = true;
        inst.connect((msg) => {
            if (this.handler) {
                try {
                    this.handler(msg);
                }
                catch (e) {
                    console.error('ChatAsync handler error', e);
                }
            }
        }, {
            onOpen: () => {
                try {
                    if (debugEnabled())
                        console.log('[ChatWS] ready');
                }
                catch { }
                this.clearReconnectTimer();
                this.reconnectAttempts = 0;
                this.setStatus({ state: 'open', attempts: 0 });
            },
            onClose: () => {
                if (!this.shouldReconnect) {
                    this.setStatus({ state: 'closed', attempts: this.reconnectAttempts });
                    return;
                }
                this.scheduleReconnect();
            },
            onError: () => { },
        });
        this.ws = inst;
        this.lastTokenSnapshot = currentToken;
    }
    async sendRequestLazy(body) {
        this.ensureConnected();
        try {
            await this.ws.waitUntilOpen?.();
        }
        catch { }
        try {
            this.ws.sendRequest(body);
        }
        catch (e) {
            // Drop socket to force reconnect on next call
            try {
                this.ws.close?.();
            }
            catch { }
            this.ws = null;
            throw e;
        }
    }
    // Immediately connect or reconnect if token changed
    connect(force) {
        this.ensureConnected(!!force);
    }
    close() {
        this.shouldReconnect = false;
        this.clearReconnectTimer();
        this.reconnectAttempts = 0;
        try {
            this.ws?.close?.();
        }
        catch { }
        this.ws = null;
        this.setStatus({ state: 'closed', attempts: 0 });
    }
    retryNow() {
        this.clearReconnectTimer();
        this.reconnectAttempts = 0;
        this.setStatus({ state: 'connecting', attempts: 0 });
        this.ensureConnected(true);
    }
    onStatus(listener) {
        this.statusListeners.add(listener);
        try {
            listener(this.status);
        }
        catch { }
        return () => { this.statusListeners.delete(listener); };
    }
    setStatus(s) {
        this.status = s;
        for (const l of this.statusListeners) {
            try {
                l(s);
            }
            catch { }
        }
    }
    scheduleReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.setStatus({ state: 'closed', attempts: this.reconnectAttempts });
            return;
        }
        this.reconnectAttempts += 1;
        const delay = [1500, 3000, 5000][Math.min(this.reconnectAttempts - 1, 2)];
        this.setStatus({ state: 'reconnecting', attempts: this.reconnectAttempts });
        this.clearReconnectTimer();
        this.reconnectTimer = window.setTimeout(() => {
            this.ensureConnected(true);
        }, delay);
    }
    clearReconnectTimer() {
        if (this.reconnectTimer) {
            try {
                window.clearTimeout(this.reconnectTimer);
            }
            catch { }
            this.reconnectTimer = null;
        }
    }
}
export const chatAsync = new ChatAsyncManager();
